import { useEffect } from 'react';

/**
 * Define título, description, robots e canonical da página.
 *
 * Por que não usar o <Helmet>: o `react-helmet-async` (2.0.5) não renderiza
 * nada neste projeto com React 19 — verificado no browser, `[data-rh]` = 0 em
 * todas as rotas, então título/description/canonical/JSON-LD por página nunca
 * chegam ao DOM. Este hook manipula as tags existentes diretamente, o que é
 * determinístico e não depende da lib.
 *
 * Ele ALTERA as tags estáticas do index.html em vez de acrescentar novas,
 * evitando meta duplicada/conflitante (importante para o `robots`), e restaura
 * os valores anteriores ao desmontar.
 */

interface PageMeta {
    title: string;
    description?: string;
    robots?: string;
    canonical?: string;
    /** URL absoluta da imagem de compartilhamento (Open Graph, 1200x630). */
    image?: string;
}

type Restore = () => void;

/** `attr` é "name" para meta comuns e "property" para as do Open Graph. */
const setMeta = (key: string, content: string, attr: 'name' | 'property' = 'name'): Restore => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    let created = false;

    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
    }

    const previous = el.getAttribute('content') ?? '';
    el.setAttribute('content', content);

    return () => {
        if (created) el?.remove();
        else el?.setAttribute('content', previous);
    };
};

const setCanonical = (href: string): Restore => {
    let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    let created = false;

    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
        created = true;
    }

    const previous = el.getAttribute('href') ?? '';
    el.setAttribute('href', href);

    return () => {
        if (created) el?.remove();
        else el?.setAttribute('href', previous);
    };
};

export const usePageMeta = ({ title, description, robots, canonical, image }: PageMeta) => {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;

        const restores: Restore[] = [];
        if (description) restores.push(setMeta('description', description));
        if (robots) restores.push(setMeta('robots', robots));
        if (canonical) restores.push(setCanonical(canonical));

        // Open Graph / Twitter — sem isto o link compartilhado (WhatsApp,
        // LinkedIn) aparece sem prévia. O index.html do projeto não traz
        // nenhuma tag OG, então elas são criadas aqui.
        restores.push(setMeta('og:type', 'website', 'property'));
        restores.push(setMeta('og:site_name', 'Pacces Advogados', 'property'));
        restores.push(setMeta('og:title', title, 'property'));
        if (description) restores.push(setMeta('og:description', description, 'property'));
        if (canonical) restores.push(setMeta('og:url', canonical, 'property'));
        if (image) {
            restores.push(setMeta('og:image', image, 'property'));
            restores.push(setMeta('og:image:width', '1200', 'property'));
            restores.push(setMeta('og:image:height', '630', 'property'));
            restores.push(setMeta('twitter:image', image));
        }
        restores.push(setMeta('twitter:card', image ? 'summary_large_image' : 'summary'));
        restores.push(setMeta('twitter:title', title));
        if (description) restores.push(setMeta('twitter:description', description));

        return () => {
            document.title = previousTitle;
            restores.forEach((restore) => restore());
        };
    }, [title, description, robots, canonical, image]);
};
